import { format, subDays, subHours } from 'date-fns'
import { Calendar } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export interface DateRange {
  from: Date
  to: Date
}

const PRESETS = [
  {
    label: 'Last 24 hours',
    value: (): DateRange => ({ from: subHours(new Date(), 24), to: new Date() }),
  },
  {
    label: 'Last 7 days',
    value: (): DateRange => ({ from: subDays(new Date(), 7), to: new Date() }),
  },
  {
    label: 'Last 30 days',
    value: (): DateRange => ({ from: subDays(new Date(), 30), to: new Date() }),
  },
  {
    label: 'Last 90 days',
    value: (): DateRange => ({ from: subDays(new Date(), 90), to: new Date() }),
  },
] as const

interface DateRangePickerProps {
  value: DateRange
  onChange: (range: DateRange) => void
}

export function DateRangePicker({ value, onChange }: DateRangePickerProps): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary hover:bg-surface-1"
      >
        <Calendar className="h-4 w-4 text-text-tertiary" />
        {format(value.from, 'MMM d')} &ndash; {format(value.to, 'MMM d, yyyy')}
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-2 w-64 rounded-xl border border-surface-3 bg-surface-0 p-3 shadow-lg">
          {PRESETS.map((preset) => (
            <button
              type="button"
              key={preset.label}
              onClick={() => {
                onChange(preset.value())
                setOpen(false)
              }}
              className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-surface-1"
            >
              {preset.label}
            </button>
          ))}
          <hr className="my-2 border-surface-3" />
          <div className="space-y-2">
            <label htmlFor="date-range-from" className="block text-xs text-text-secondary">
              From
            </label>
            <input
              id="date-range-from"
              type="date"
              value={format(value.from, 'yyyy-MM-dd')}
              onChange={(e) => onChange({ ...value, from: new Date(e.target.value) })}
              className="w-full rounded-lg border border-surface-3 px-3 py-1.5 text-sm"
            />
            <label htmlFor="date-range-to" className="block text-xs text-text-secondary">
              To
            </label>
            <input
              id="date-range-to"
              type="date"
              value={format(value.to, 'yyyy-MM-dd')}
              onChange={(e) => onChange({ ...value, to: new Date(e.target.value) })}
              className="w-full rounded-lg border border-surface-3 px-3 py-1.5 text-sm"
            />
          </div>
        </div>
      )}
    </div>
  )
}
